type AiAgentModule = typeof import('@material-bank/ie-search');

const ROOT_ID = 'mb-ie-search-root';
const AI_AGENT_CUSTOMER_SECTION_RELOAD_SOURCE = 'mb-ie-search-add-to-cart';
const PRESERVE_AGENT_OPEN_TIMEOUT_MS = 10_000;
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

// One-time auto-open ("tease"): the first time an eligible customer lands on the
// homepage, open AI Search by itself once to nudge them into trying it (the CTA
// pill wasn't drawing clicks). Capped at TWO attempts ever, never more --
// see AUTO_OPEN_STATE_* below. Bump the version suffix to re-tease everyone in
// a future campaign.
const AUTO_OPEN_STORAGE_KEY_PREFIX = 'mb-ie-search-auto-open-v1';
const IE_SEARCH_BUTTON_ID = 'mb-ie-search-button';
// Widget -> host notification (detail: { openDurationMs }), fired whenever the
// modal actually closes, by any means (see @material-bank/ie-search's
// IE_SEARCH_CLOSED_EVENT -- not imported here, same pattern as
// IE_SEARCH_BUTTON_ID above: this file talks to the widget by DOM id / raw
// event name, not package imports).
const IE_SEARCH_CLOSED_EVENT = 'mb-ie-search:closed';
// The widget unhides + wires the button asynchronously after mount; poll for it.
const AUTO_OPEN_BUTTON_WAIT_MS = 8_000;
const AUTO_OPEN_POLL_INTERVAL_MS = 150;
// Read as an invitation, not an ambush: wait a beat after landing before
// popping, even if the button is ready sooner.
const AUTO_OPEN_MIN_DELAY_MS = 2_500;
// A close this fast after opening means they didn't really register it
// (reflexive dismiss, tab-switch, etc.) -- not a genuine "not interested".
const AUTO_OPEN_QUICK_CLOSE_MS = 1_500;
// Storage values for AUTO_OPEN_STORAGE_KEY_PREFIX:<customerId>. '1' is the
// ORIGINAL terminal value (kept for backward compat with customers already
// teased by the one-shot version of this feature) -- never rename it.
const AUTO_OPEN_STATE_DONE = '1';
// Opened once, closed within AUTO_OPEN_QUICK_CLOSE_MS: eligible for exactly
// ONE more attempt on a later visit, never a third.
const AUTO_OPEN_STATE_RETRY = 'retry';
// Set right before clicking, before we know the outcome -- protects against a
// reload mid-open (see the PENDING handling in maybeAutoOpenOnce): a page load
// that finds PENDING left over from an EARLIER load (not its own) never
// re-opens on the strength of it, it just finalizes to DONE.
const AUTO_OPEN_STATE_PENDING = 'pending';

let agentModule: AiAgentModule | undefined;
let agentModulePromise: Promise<AiAgentModule> | undefined;
let resolvedAppUrl: string | undefined;
let isAgentMounted = false;
let preserveMountedAgentOnNextPrivateContentLoad = false;
let preserveAgentOpenTimeoutId: number | undefined;
let renderRequestId = 0;
// Guards the auto-open to a single attempt per page load (render() can fire
// several times). Persistence across loads is handled by localStorage.
let hasAutoOpenAttempted = false;

function getRootElement() {
  const rootElement = document.getElementById(ROOT_ID);
  if (!rootElement) return null;

  return rootElement;
}

type MagentoCustomer = {
  customer_id?: string | number;
  id?: string | number;
  signin_token?: string;
  current_project?: string | number;
};

function getMagentoCustomerValue(customer: object, key: keyof MagentoCustomer) {
  const value = Object.entries(customer).find(
    ([candidateKey]) => candidateKey === key,
  )?.[1];

  return typeof value === 'string' || typeof value === 'number'
    ? value
    : undefined;
}

function getMagentoCustomer(): MagentoCustomer | null {
  try {
    const mageCacheStorage: unknown = JSON.parse(
      window.localStorage.getItem('mage-cache-storage') || '{}',
    );
    if (!mageCacheStorage || typeof mageCacheStorage !== 'object') return null;

    const customer = Object.entries(mageCacheStorage).find(
      ([key]) => key === 'customer',
    )?.[1];
    if (!customer || typeof customer !== 'object') return null;

    const signinToken = getMagentoCustomerValue(customer, 'signin_token');

    return {
      customer_id: getMagentoCustomerValue(customer, 'customer_id'),
      id: getMagentoCustomerValue(customer, 'id'),
      signin_token: typeof signinToken === 'string' ? signinToken : undefined,
      current_project: getMagentoCustomerValue(customer, 'current_project'),
    };
  } catch {
    return null;
  }
}

function getMagentoStorageToken() {
  const directToken = window.localStorage.getItem('authToken');
  if (directToken) return directToken;

  return getMagentoCustomer()?.signin_token || null;
}

function normalizeCustomerId(value: unknown) {
  const customerId = String(value ?? '')
    .trim()
    .replace(/^0+(?=\d)/, '');

  return /^[1-9]\d*$/.test(customerId) ? customerId : '';
}

function getEnabledCustomerIds(rootElement: HTMLElement) {
  return (
    rootElement.dataset.enabledCustomerIds
      ?.split(/[\s,]+/)
      .map(normalizeCustomerId)
      .filter(Boolean) || []
  );
}

function isCustomerAuthenticated() {
  return Boolean(getMagentoStorageToken());
}

function isCustomerEnabled(rootElement: HTMLElement) {
  const enabledCustomerIds = getEnabledCustomerIds(rootElement);
  if (enabledCustomerIds.length === 0) return true;

  const customer = getMagentoCustomer();
  const customerId = normalizeCustomerId(customer?.customer_id ?? customer?.id);
  return Boolean(customerId && enabledCustomerIds.includes(customerId));
}

// Add-to-cart is project-scoped, so a logged-in customer with no current project
// can't order anything from the embed (they'd only ever hit "select a project").
// Until the in-embed project selector ships, gate the CTA off entirely for them.
function hasCurrentProject() {
  const projectId = String(getMagentoCustomer()?.current_project ?? '').trim();
  return Boolean(projectId && projectId !== '0');
}

function isDesktopViewport() {
  return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
}

function hasAppUrlSource(rootElement: HTMLElement) {
  return Boolean(rootElement.dataset.embedUrlEndpoint?.trim());
}

function shouldMountAgent(rootElement: HTMLElement) {
  return Boolean(
    hasAppUrlSource(rootElement) &&
      isDesktopViewport() &&
      isCustomerAuthenticated() &&
      isCustomerEnabled(rootElement) &&
      hasCurrentProject(),
  );
}

/**
 * Resolves the embedded AI Search URL (…?embed=1#token=<jwt>).
 *
 * ALWAYS fetched from the non-cacheable react/aiagent/embedurl endpoint. A
 * server-rendered data-app-url is deliberately not honoured: this block renders
 * on full-page-cacheable pages, so a baked-in token is shared by everyone
 * served that cached object and dies when it expires (12h), which silently
 * blanks the modal. Fetching per page load keeps the token fresh and
 * per-customer. The result is cached for the lifetime of the page.
 */
async function resolveAppUrl(rootElement: HTMLElement) {
  if (resolvedAppUrl) return resolvedAppUrl;

  const endpoint = rootElement.dataset.embedUrlEndpoint?.trim();
  if (!endpoint) return '';

  try {
    const response = await fetch(endpoint, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      console.warn(
        `AI Search embed URL request failed with status ${response.status}.`,
      );
      return '';
    }

    const data: unknown = await response.json();
    if (!data || typeof data !== 'object') return '';
    const appUrl = Object.entries(data).find(([key]) => key === 'appUrl')?.[1];
    if (typeof appUrl !== 'string' || !appUrl.trim()) return '';

    resolvedAppUrl = appUrl.trim();
    return resolvedAppUrl;
  } catch {
    return '';
  }
}

function loadAgentModule() {
  if (!agentModulePromise) {
    agentModulePromise = import('@material-bank/ie-search')
      .then((loadedAgentModule) => {
        agentModule = loadedAgentModule;
        return loadedAgentModule;
      })
      .catch((error: unknown) => {
        agentModulePromise = undefined;
        throw error;
      });
  }

  return agentModulePromise;
}

function unmountAgent(rootElement: HTMLElement) {
  if (isAgentMounted) {
    // React effect cleanup disconnects the agent socket and clears its state.
    agentModule?.unmountIeSearch(rootElement);
  }

  isAgentMounted = false;
}

function clearPreservedMountedAgent() {
  preserveMountedAgentOnNextPrivateContentLoad = false;

  if (preserveAgentOpenTimeoutId !== undefined) {
    window.clearTimeout(preserveAgentOpenTimeoutId);
    preserveAgentOpenTimeoutId = undefined;
  }
}

function shouldPreserveMountedAgent(event: Event) {
  if (!(event instanceof CustomEvent)) return false;

  const detail = event.detail;
  return (
    Boolean(detail) &&
    typeof detail === 'object' &&
    Object.entries(detail).some(
      ([key, value]) =>
        key === 'source' && value === AI_AGENT_CUSTOMER_SECTION_RELOAD_SOURCE,
    )
  );
}

// "Landing on the site" = the homepage, including EU locale roots (/en, /fr,
// /en-us). Deliberately not deeper pages, so we never pop the modal on top of a
// PDP, cart or checkout flow.
function isHomepageLanding() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return path === '/' || /^\/[a-z]{2}(-[a-z]{2})?$/i.test(path);
}

function getAutoOpenStorageKey() {
  const customer = getMagentoCustomer();
  const customerId = normalizeCustomerId(customer?.customer_id ?? customer?.id);
  return customerId ? `${AUTO_OPEN_STORAGE_KEY_PREFIX}:${customerId}` : null;
}

function readAutoOpenState(storageKey: string) {
  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function writeAutoOpenState(storageKey: string, state: string) {
  try {
    window.localStorage.setItem(storageKey, state);
  } catch {
    // Private mode / storage disabled — skip persistence; worst case the tease
    // shows again on a later load, which is acceptable for a nudge.
  }
}

/**
 * Clicks the (already-ready) trigger button and observes how the modal
 * closes, to decide whether this attempt earns a retry. Persists PENDING
 * before clicking so a reload mid-open can't double-tease; the eventual
 * outcome overwrites it with RETRY or DONE.
 */
function openAndObserveClose(
  storageKey: string,
  isRetryAttempt: boolean,
  button: HTMLElement,
) {
  writeAutoOpenState(storageKey, AUTO_OPEN_STATE_PENDING);

  const onClosed = (event: Event) => {
    window.removeEventListener(IE_SEARCH_CLOSED_EVENT, onClosed);

    const detail = event instanceof CustomEvent ? event.detail : undefined;
    const openDurationMs =
      typeof detail?.openDurationMs === 'number'
        ? detail.openDurationMs
        : Infinity;

    // A quick bounce on the FIRST attempt didn't really register — give it
    // exactly one more try on a later visit. Any other outcome (a real look,
    // or the retry attempt itself, win or lose) is terminal.
    const finalState =
      !isRetryAttempt && openDurationMs < AUTO_OPEN_QUICK_CLOSE_MS
        ? AUTO_OPEN_STATE_RETRY
        : AUTO_OPEN_STATE_DONE;

    writeAutoOpenState(storageKey, finalState);
  };

  window.addEventListener(IE_SEARCH_CLOSED_EVENT, onClosed);
  button.click();
}

/**
 * Opens AI Search by itself, to nudge a first-time eligible customer on the
 * homepage — capped at two attempts ever (see AUTO_OPEN_STATE_* above).
 * Eligibility (desktop, authenticated, enabled customer, has a current
 * project) is already guaranteed by shouldMountAgent() before this runs. We
 * drive the widget's own trigger — clicking the hidden #mb-ie-search-button,
 * which the ie-search widget unhides and wires once mounted — rather than
 * reaching into the external module's internals.
 */
function maybeAutoOpenOnce(rootElement: HTMLElement) {
  if (hasAutoOpenAttempted || !isHomepageLanding()) return;

  const storageKey = getAutoOpenStorageKey();
  if (!storageKey) return;

  const state = readAutoOpenState(storageKey);
  if (state === AUTO_OPEN_STATE_DONE) return;

  // PENDING left over from an EARLIER page load means we opened it there and
  // never observed how it closed (e.g. that page reloaded mid-open) — finalize
  // as done rather than risk re-popping on every reload.
  if (state === AUTO_OPEN_STATE_PENDING) {
    writeAutoOpenState(storageKey, AUTO_OPEN_STATE_DONE);
    return;
  }

  const isRetryAttempt = state === AUTO_OPEN_STATE_RETRY;

  // One attempt per page load regardless of outcome; if the button never
  // becomes ready we simply don't persist, so a fresh load can retry.
  hasAutoOpenAttempted = true;

  const earliestOpenAt = Date.now() + AUTO_OPEN_MIN_DELAY_MS;
  const deadline = Date.now() + AUTO_OPEN_BUTTON_WAIT_MS;

  const tryOpen = () => {
    // Re-check gating in case auth/project/viewport changed while we waited.
    if (!isAgentMounted || !shouldMountAgent(rootElement)) return;

    const button = document.getElementById(IE_SEARCH_BUTTON_ID);
    // The widget clears `hidden` once it's mounted and its click handler is
    // wired — treat that as the readiness signal. Read as an invitation, not
    // an ambush: hold off clicking until AUTO_OPEN_MIN_DELAY_MS has passed
    // even if the button was ready sooner.
    if (button && !button.hidden && Date.now() >= earliestOpenAt) {
      openAndObserveClose(storageKey, isRetryAttempt, button);
      return;
    }

    if (Date.now() < deadline) {
      window.setTimeout(tryOpen, AUTO_OPEN_POLL_INTERVAL_MS);
    }
  };

  tryOpen();
}

async function render() {
  const currentRenderRequestId = ++renderRequestId;
  const rootElement = getRootElement();
  if (!rootElement) return;

  if (!shouldMountAgent(rootElement)) {
    unmountAgent(rootElement);
    return;
  }

  let loadedAgentModule: AiAgentModule;

  try {
    loadedAgentModule = await loadAgentModule();
  } catch (error: unknown) {
    console.error('Failed to load the AI agent bundle.', error);
    return;
  }

  const appUrl = await resolveAppUrl(rootElement);
  if (!appUrl) return;

  if (
    currentRenderRequestId !== renderRequestId ||
    !rootElement.isConnected ||
    !shouldMountAgent(rootElement)
  ) {
    return;
  }

  loadedAgentModule.mountIeSearch(rootElement, { appUrl });
  isAgentMounted = true;

  maybeAutoOpenOnce(rootElement);
}

function handleCustomerSectionReload(event: Event) {
  if (!shouldPreserveMountedAgent(event)) return;

  clearPreservedMountedAgent();
  preserveMountedAgentOnNextPrivateContentLoad = true;
  preserveAgentOpenTimeoutId = window.setTimeout(
    clearPreservedMountedAgent,
    PRESERVE_AGENT_OPEN_TIMEOUT_MS,
  );
}

function handlePrivateContentLoaded() {
  const shouldPreserve =
    preserveMountedAgentOnNextPrivateContentLoad && isAgentMounted;

  clearPreservedMountedAgent();

  if (!shouldPreserve) {
    // Hyvä dispatches this event before persisting the fetched customer data.
    // Render in the next task so mage-cache-storage contains signin_token.
    window.setTimeout(() => void render(), 0);
  }
}

void render();
window.addEventListener(
  'reload-customer-section-data',
  handleCustomerSectionReload,
);
window.addEventListener('private-content-loaded', handlePrivateContentLoaded);
window
  .matchMedia(DESKTOP_MEDIA_QUERY)
  .addEventListener('change', () => void render());
